import { createContext, useContext, useEffect, useRef, useState } from "react";

const OBJECT_STORE_NAME = "hooked-storage";
const DB_NAME = "hooked-storage-db";

const DbContext = createContext<IDBDatabase | null>(null);

export function IndexDbStorageProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [db, setDb] = useState<IDBDatabase | null>(null);
  useEffect(() => {
    if (!window.indexedDB) {
      console.error("IndexedDB is not supported in this browser.");
      return;
    }
    if (db !== null) {
      return;
    }
    const request = window.indexedDB.open(DB_NAME, 1);
    request.onerror = (event) => {
      console.error("Database error:", event);
    };
    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      setDb(db);
    };
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(OBJECT_STORE_NAME)) {
        db.createObjectStore(OBJECT_STORE_NAME, { keyPath: "id" });
      }
    };
  }, [db]);

  return <DbContext.Provider value={db}>{children}</DbContext.Provider>;
}

export function useIndexDbStorage<T>(key: string, initialValue: T) {
  const db = useContext(DbContext);
  const [value, setValue] = useState<T>(initialValue);
  const loadState = useRef<"pending" | "loading" | "ready">("pending");

  // Load from IndexedDB on mount
  useEffect(() => {
    if (!db || loadState.current !== "pending") {
      return;
    }
    loadState.current = "loading";
    const transaction = db.transaction(OBJECT_STORE_NAME, "readwrite");
    const store = transaction.objectStore(OBJECT_STORE_NAME);

    const getRequest = store.get(key);
    getRequest.onsuccess = (event) => {
      const result = (event.target as IDBRequest).result;
      if (typeof result !== "undefined") {
        setValue(result.value);
      } else {
        store.put({ id: key, value: initialValue });
      }
      loadState.current = "ready";
    };
    getRequest.onerror = (event) => {
      console.error("Failed to retrieve value:", event);
      loadState.current = "ready";
    };
  }, [db, key, initialValue]);

  // Sync to IndexedDB when value changes (only after initial load)
  useEffect(() => {
    if (!db || loadState.current !== "ready") {
      return;
    }
    const transaction = db.transaction(OBJECT_STORE_NAME, "readwrite");
    const store = transaction.objectStore(OBJECT_STORE_NAME);
    store.put({ id: key, value });
  }, [value, db, key]);

  return [value, setValue] as const;
}
